import { useQuery } from '@tanstack/react-query';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { getTrailName } from '@/constants/trails';
import { trackProductView } from '@/lib/activity-tracker';
import { useEffect } from 'react';

// 상품 정보 타입 정의
interface ProductData {
  product_id: number;
  name: string;
  description?: string;
  price: number;
  duration_days: number;
  difficulty_level: number;
  max_participants: number;
  min_participants: number;
  includes: string[];
  excludes: string[];
  pickup_locations: string[];
  images?: string[];
  created_at: string;
  updated_at: string;
}

// 코스 정보 타입 정의
interface CourseData {
  course_id: number;
  trail_id: number;
  name: string;
  description: string;
  distance_km: number;
  duration_hours: number;
  difficulty_level: number;
  elevation_gain_m?: number;
  highlights?: string[];
  gpx_file_path?: string;
  created_at: string;
  updated_at: string;
  product_courses?: Array<{
    product_id: number;
    products: ProductData;
  }>;
}

// 트레일 정보 타입 정의 (간소화된 형태)
interface TrailData {
  name: string;
}

export interface ProductDetailData {
  product: ProductData;
  course: CourseData;
  trail: TrailData;
}

/**
 * 상품 상세 정보 조회 훅
 * @param courseId - 코스 ID (product detail URL의 id 파라미터)
 */
export function useProductDetailData(courseId: string | undefined) {
  const { supabase } = useSupabaseConnection();
  
  const query = useQuery<ProductDetailData>({
    queryKey: ['productDetail', courseId],
    queryFn: async () => {
      if (!courseId) throw new Error('Course ID is required');
      
      // 1. Get course, product, and trail data
      const { data: courseData, error: courseError } = await supabase
        .from("courses")
        .select(
          `
          *,
          product_courses!inner(
            product_id,
            products!inner(*)
          )
        `
        )
        .eq("course_id", courseId)
        .maybeSingle();

      if (courseError) {
        console.error("Error fetching course data:", courseError);
        throw courseError;
      }

      if (!courseData) {
        throw new Error('Product not found');
      }

      // trails 조인 대신 상수 사용으로 성능 최적화
      const trailName = getTrailName(courseData.trail_id);
      const trail = { name: trailName };
      
      const productData = courseData.product_courses?.[0]?.products;
      if (!productData) {
        throw new Error('Product data not found');
      }

      return {
        product: productData,
        course: courseData,
        trail: trail
      };
    },
    enabled: !!courseId,
    
    // OPTIMIZATION: Increase stale time to prevent unnecessary refetch on visibility changes
    staleTime: 10 * 60 * 1000, // 10분간 캐시 유지 (증가)
    
    // OPTIMIZATION: Reduce garbage collection time to free memory faster
    gcTime: 15 * 60 * 1000, // 15분 (기본값보다 길게)
    
    // OPTIMIZATION: Disable automatic refetch behaviors that cause loops
    refetchOnWindowFocus: false, // 브라우저 포커스시 refetch 비활성화
    refetchOnReconnect: false,   // 재연결시 refetch 비활성화
    refetchOnMount: true,        // 마운트시에만 refetch
    
    retry: 2, // 재시도 횟수 증가 (안정성)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // 재시도 딜레이
  });

  // 상품 조회 추적 (성공적으로 로드되었을 때만)
  useEffect(() => {
    if (query.data?.product) {
      trackProductView(query.data.product.product_id, query.data.product.name);
    }
  }, [query.data?.product]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ? '상품 정보를 불러오는 중 오류가 발생했습니다.' : null,
    refetch: query.refetch,
  };
}